import React, { useState } from "react";
import { discountGroupObjects, store_availability_fields } from "../data";
import { Box, Button, Typography } from "@mui/material";
import { arrType } from "data/globalData";
import RenderInputField from "components/MboPaymentWorking/CommonComponents/RenderInputFiled/RenderInputFiled";
import { getStoreDisplayName, normalizeValue } from "../DiscountSummary";
import StorePickerDialog from "./StorePickerDialog";

export const getUniqueFieldData = (data = [], field) => {
	return [...new Set(data?.map((item) => item[field]))]
		.filter(Boolean)
		.map((value) => ({
			name: value,
			key: value,
		}));
};

const getUniqueValues = (items = [], getter) =>
	[
		...new Set(
			items
				.map((item) => (typeof getter === "function" ? getter(item) : item?.[getter]))
				.map((value) => (value == null ? "" : String(value).trim()))
				.filter(Boolean)
		),
	];

const getPartnerName = (item) =>
	item?.partner_site_name || item?.partner_name || item?.partnerName || item?.partner || "";

function StoreAvailabilitySection({ value, setValue, disable, data }) {
	const storeAvailabilityKey = discountGroupObjects.store_availability.key;
	const storeAvailability = value?.[storeAvailabilityKey] || {};
	const [storePickerOpen, setStorePickerOpen] = useState(false);

	const isChannelMBO = value?.[discountGroupObjects.brand_channel.key]?.channel === "MBO";
	const isChannelLFS = value?.[discountGroupObjects.brand_channel.key]?.channel === "LFS";

	let partnerData = [];
	let zoneData = [];
	let stateData = [];
	let cityData = [];
	let storeGradeData = [];

	if (isChannelLFS && storeAvailability?.partner?.length) {
		partnerData = data.filter((item) => storeAvailability.partner.includes(getPartnerName(item)));
	} else {
		partnerData = data;
	}

	if (isChannelMBO && storeAvailability?.distributor?.length) {
		zoneData = partnerData.filter((item) => storeAvailability.distributor.includes(item?.distributor));
	} else {
		zoneData = partnerData;
	}

	if (storeAvailability?.zone?.length) {
		stateData = zoneData.filter((item) => storeAvailability.zone.includes(item?.zone));
	}
	if (storeAvailability?.state?.length) {
		cityData = stateData.filter((item) => storeAvailability.state.includes(item?.state));
	}
	if (storeAvailability?.city?.length) {
		storeGradeData = cityData.filter((item) => storeAvailability.city.includes(item?.city));
	}

	// Store list is always available from partner/distributor-filtered data so users can
	// search stores directly without selecting zone → state → city → grade first.
	const storePool = zoneData;
	const selectedStoreGrades = (storeAvailability?.store_grade || [])
		.map((grade) => String(grade || "").trim())
		.filter(Boolean);
	const storeOptionPool = selectedStoreGrades.length
		? storePool.filter((item) => selectedStoreGrades.includes(String(item?.store_grade || "").trim()))
		: storePool;

	const filterStoresByGrades = (stores = [], grades = []) => {
		if (!grades.length) return stores;
		const allowedNames = new Set(
			storePool
				.filter((item) => grades.includes(String(item?.store_grade || "").trim()))
				.map((item) => normalizeValue(getStoreDisplayName(item)))
				.filter(Boolean)
		);
		return stores.filter((storeName) =>
			allowedNames.has(
				normalizeValue(typeof storeName === "object" ? getStoreDisplayName(storeName) : storeName)
			)
		);
	};

	const buildValueForStoreSelection = (
		baseValue,
		baseStoreAvailability,
		selectedStores,
		{ limitToSelectedGrades = false } = {}
	) => {
		const grades = (baseStoreAvailability.store_grade || [])
			.map((grade) => String(grade || "").trim())
			.filter(Boolean);
		const storesForMatch =
			limitToSelectedGrades && grades.length ? filterStoresByGrades(selectedStores, grades) : selectedStores;
		const selectedStoreSet = new Set(storesForMatch.map(normalizeValue).filter(Boolean));
		const matchedStores = selectedStoreSet.size
			? storePool.filter((item) => selectedStoreSet.has(normalizeValue(getStoreDisplayName(item))))
			: [];

		if (!matchedStores.length) {
			return {
				...baseValue,
				[storeAvailabilityKey]: {
					...baseStoreAvailability,
					store: storesForMatch,
				},
			};
		}

		const derivedGrades = getUniqueValues(matchedStores, "store_grade");

		return {
			...baseValue,
			[storeAvailabilityKey]: {
				...baseStoreAvailability,
				store: storesForMatch,
				zone: getUniqueValues(matchedStores, "zone"),
				state: getUniqueValues(matchedStores, "state"),
				city: getUniqueValues(matchedStores, "city"),
				store_grade:
					limitToSelectedGrades && grades.length
						? derivedGrades.filter((grade) => grades.includes(grade))
						: derivedGrades,
				...(isChannelLFS ? { partner: getUniqueValues(matchedStores, getPartnerName) } : {}),
				...(isChannelMBO ? { distributor: getUniqueValues(matchedStores, "distributor") } : {}),
			},
		};
	};

	const applyLocationFromStores = (nextValue) => {
		// RenderInputField shallow-copies and mutates the nested store_availability object,
		// so this handler is only wired for the Store field and always treats the call as a store change.
		const nextStoreAvailability = nextValue?.[storeAvailabilityKey] || {};
		setValue(
			buildValueForStoreSelection(
				nextValue,
				nextStoreAvailability,
				[...(nextStoreAvailability?.store || [])],
				{ limitToSelectedGrades: true }
			)
		);
	};

	const applyStoreGradeChange = (nextValue) => {
		const nextStoreAvailability = nextValue?.[storeAvailabilityKey] || {};
		const grades = (nextStoreAvailability.store_grade || [])
			.map((grade) => String(grade || "").trim())
			.filter(Boolean);

		setValue({
			...nextValue,
			[storeAvailabilityKey]: {
				...nextStoreAvailability,
				store_grade: grades,
				store: [],
			},
		});
	};

	const handleBulkStoreApply = (selectedStores = []) => {
		setValue(buildValueForStoreSelection(value, { ...storeAvailability }, selectedStores));
		setStorePickerOpen(false);
	};

	return (
		<Box pl={2} width="90%">
			<Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} ml={0.5} columnGap={2}>
				<Typography variant="caption" color="text.secondary">
					Tip: Search and select stores directly — Zone, State, City and Store Grade will fill automatically.
				</Typography>
				<Button
					variant="contained"
					size="small"
					disabled={disable}
					onClick={() => setStorePickerOpen(true)}
					sx={{ textTransform: "none", fontSize: "11px", borderRadius: "8px", whiteSpace: "nowrap" }}
				>
					Bulk Select Stores
				</Button>
			</Box>
			<StorePickerDialog
				open={storePickerOpen}
				onClose={() => setStorePickerOpen(false)}
				onApply={handleBulkStoreApply}
				storePool={storePool}
				selectedStores={storeAvailability?.store || []}
				selectedGrades={selectedStoreGrades}
			/>
			<Box display="flex" columnGap={"2%"} rowGap="10px" flexWrap="wrap">
				{store_availability_fields?.map((row) => {
					const column = {
						...row,
						constraints: {
							...row.constraints,
							DISPLAY:
								row?.key === "distributor"
									? isChannelMBO
									: row?.key === "partner"
										? isChannelLFS
										: true,
						},
					};
					let option_data = row.option_data ? row.option_data : {};

					if (row?.key === "partner") {
						option_data = {
							type: arrType,
							list: getUniqueFieldData(
								data.map((item) => ({
									partner_name: getPartnerName(item),
								})),
								"partner_name"
							),
						};
					}
					if (row?.key === "distributor") {
						option_data = {
							type: arrType,
							list: getUniqueFieldData(partnerData, "distributor"),
						};
					}
					if (row?.key === "zone") {
						option_data = {
							type: arrType,
							list: getUniqueFieldData(zoneData, "zone"),
						};
					}
					if (row?.key === "state") {
						option_data = {
							type: arrType,
							list: getUniqueFieldData(stateData, "state"),
						};
					}
					if (row?.key === "city") {
						option_data = {
							type: arrType,
							list: getUniqueFieldData(cityData, "city"),
						};
					}
					if (row?.key === "store_grade") {
						option_data = {
							type: arrType,
							list: getUniqueFieldData(storeGradeData, "store_grade"),
						};
					}
					if (row?.key === "store") {
						option_data = {
							type: arrType,
							list: getUniqueFieldData(
								storeOptionPool.map((item) => ({
									store_name: getStoreDisplayName(item),
								})),
								"store_name"
							),
						};
					}
					const setFieldValue =
						row?.key === "store"
							? applyLocationFromStores
							: row?.key === "store_grade"
								? applyStoreGradeChange
								: setValue;
					return (
						<RenderInputField
							column={{ ...column, option_data }}
							data={value}
							setData={setFieldValue}
							width="49%"
							selectOptObjName={"option_data"}
							key={column.key}
							disabled={disable}
						/>
					);
				})}
			</Box>
		</Box>
	);
}

export default StoreAvailabilitySection;
